import { useEffect, useState } from "react";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link2, Download, ShieldCheck, CheckCircle2, AlertCircle, RefreshCcw } from "lucide-react";
import api, { apiErr } from "@/lib/api";

const TEMPLATE_COLUMNS = [
  "NEXREQ Record ID", "NEXREQ Lead Group ID", "Research Date", "Company Name", "Company Website",
  "Company LinkedIn URL", "Industry", "Open Position", "Job Location", "Country/Region",
  "Job Posting URL", "POC Name", "POC Designation", "POC Department", "POC Email", "POC Phone",
  "POC Extension", "POC LinkedIn URL", "Primary POC", "Outreach Status", "Email Sent From",
  "Email Sent To", "Email CC", "Email Subject", "Email Body", "Email Sent Date", "Email Sent Time",
  "Time Zone", "Follow-Up Date", "Reply Received", "Reply Type", "Reply Subject", "Reply Content",
  "Reply Date", "Reply Time", "R.A. Notes", "Evidence/Attachment URL", "Sync Status",
  "Last Sync Date", "Sync Error",
];

export default function RASyncCenter() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [url, setUrl] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/google-sheets/status");
      setStatus(data);
      setUrl(data?.spreadsheet_url || "");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const linkSheet = async () => {
    if (!url) return toast.error("Paste your Google Sheet URL first");
    setSaving(true);
    try {
      const { data } = await api.post("/google-sheets/link", { spreadsheet_url: url });
      setStatus(data);
      toast.success("Spreadsheet linked");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/google-sheets/disconnect");
      setStatus(data);
      setUrl("");
      toast.success("Disconnected");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = () => {
    const csv = TEMPLATE_COLUMNS.map((c) => `"${c}"`).join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const link = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = link; a.download = "NEXREQ_RA_Sheet_Template.csv"; a.click();
    URL.revokeObjectURL(link);
    toast.success("Template downloaded");
  };

  const connected = status?.connected;
  const envReady = status?.env_ready;

  return (
    <div>
      <PageHeader
        title="Google Sheets Sync Center"
        subtitle="Connect R.A. working sheets to NEXREQ (read-only sync of research, outreach & replies). The 30-day duplicate rule stays enforced by the ATS."
      />

      <Card className="p-6 max-w-3xl" data-testid="ra-sync-status-card">
        <div className="flex items-start gap-3">
          <div className={`h-12 w-12 rounded-xl grid place-items-center ${connected ? "bg-emerald-50 text-emerald-600" : envReady ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`}>
            {connected ? <CheckCircle2 className="h-6 w-6" /> : <AlertCircle className="h-6 w-6" />}
          </div>
          <div className="flex-1">
            <div className="text-base font-display font-semibold">
              {loading ? "Loading…" : connected ? "Connected to Google Sheets" : envReady ? "Not connected" : "Missing platform credentials"}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{loading ? "…" : status?.connect_hint}</p>
            {connected && (
              <p className="text-xs text-muted-foreground mt-2">
                Sheet: <code className="text-xs">{status?.spreadsheet_id}</code>
                {status?.authorized_by && <> · authorized by {status?.authorized_by}</>}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="ra-sync-refresh">
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        <div className="mt-6 space-y-3">
          <Label htmlFor="sheet-url">Google Sheet URL</Label>
          <div className="flex gap-2">
            <Input
              id="sheet-url"
              placeholder="https://docs.google.com/spreadsheets/d/…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={!envReady || saving}
              data-testid="ra-sync-sheet-url"
            />
            {connected ? (
              <Button variant="outline" onClick={disconnect} disabled={saving} data-testid="ra-sync-disconnect">
                Disconnect
              </Button>
            ) : (
              <Button
                className="bg-[#255058] hover:bg-[#1c3d43] text-white gap-1.5"
                onClick={linkSheet}
                disabled={!envReady || saving || !url}
                data-testid="ra-sync-connect"
              >
                <Link2 className="h-4 w-4" /> Connect
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={downloadTemplate} className="gap-1.5" data-testid="ra-sync-download-template">
            <Download className="h-4 w-4" /> Download Sheet Template
          </Button>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4 mt-4 max-w-3xl">
        <Card className="p-5">
          <div className="flex items-center gap-2 font-medium mb-2">
            <ShieldCheck className="h-4 w-4 text-[#255058]" /> What the sync does (unchanged workflow)
          </div>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-4">
            <li>R.A. work stays on the sheet — NEXREQ ingests read-only.</li>
            <li>Idempotent Record ID keeps rows deduplicated across runs.</li>
            <li>30-day duplicate rule enforced server-side against the ATS.</li>
            <li>Errors surface here with a row-level explanation.</li>
          </ul>
        </Card>
        <Card className="p-5">
          <div className="font-medium mb-2">Required Google scopes</div>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-4">
            {(status?.required_scopes || []).map((s) => (
              <li key={s}><code className="text-xs">{s}</code></li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-2">
            Credentials live server-side and are revocable. Only Super Admin can connect or disconnect.
          </p>
        </Card>
      </div>
    </div>
  );
}
